import { useState, type FC } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { CloseOutline, Information } from '@mint-lib/icons';
import { enqueueSnackbar } from '@mint-lib/notifications';
import {
  Button,
  Chip,
  Divider,
  typographyVariants,
  CircularLoadingButton,
} from '@mint-lib/tw-components';

import useWithdrawRequestMutation from '../../api/mutations/useWithdrawRequestMutation.js';
import { EntitySpecificFields } from '../../types/shared.js';
import StatusDetailsModal from '../modals/StatusDetailsModal/StatusDetailsModal.jsx';

type Props = EntitySpecificFields & {
  approvalProcessId: string | null;
};

const RequesterPending: FC<Props> = ({ approvalProcessId }) => {
  const { t } = useTranslation('@mint-lib/approval-flow-circuit_v2');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { isSuccess, mutateAsync, isPending } = useWithdrawRequestMutation();

  const showModal = () => {
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setIsModalOpen(false);
  };

  const handleWithdrawn = async () => {
    if (!approvalProcessId) {
      return;
    }

    try {
      await mutateAsync({
        urlParams: {
          requestId: approvalProcessId,
        },
      });
      enqueueSnackbar({
        variant: 'success',
        title: t('Your approval request has been successfully withdrawn.'),
        removable: true,
      });
    } catch {
      enqueueSnackbar({
        variant: 'error',
        title: t('Your approval request has not been withdrawn.'),
        removable: true,
      });
    }
  };

  return (
    <>
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-2">
          <span
            className={typographyVariants({
              variant: 'caption1',
              color: 'secondary',
            })}
          >
            {t('Status')}:
          </span>
          <Chip
            className="cursor-pointer"
            variant="outline"
            color="info"
            size="medium"
            onClick={showModal}
          >
            <Information />
            {t('Pending')}
          </Chip>
        </div>
        <Divider orientation="vertical" className="max-h-6" />
        <Button
          data-dd-action-name="Withdraw the flow"
          className="min-w-40"
          variant="outlined"
          color="error"
          size="medium"
          disabled={isPending || isSuccess}
          onClick={handleWithdrawn}
        >
          {isPending ? (
            <CircularLoadingButton size="small" variant="outlined" />
          ) : (
            <CloseOutline />
          )}
          {t('Withdraw request')}
        </Button>
      </div>
      <StatusDetailsModal
        open={isModalOpen}
        onClose={closeModal}
        approvalProcessId={approvalProcessId}
        userStatus="requester"
      />
    </>
  );
};

export default RequesterPending;
